export enum PortalAuthErrorCode {
  ClaimsSyncFailed = 'claimsSyncFailed'
}

const PENDING_LOGIN_CONTEXT_KEY = 'portal_login_pending_context'
/** localStorage mirror so email-link return works in a new tab (sessionStorage is per-tab). */
const PENDING_LOGIN_EMAIL_CONTEXT_KEY = 'portal_login_pending_email_context'
const PORTAL_AUTH_ERROR_KEY = 'portal_login_auth_error'

/** Pending login context TTL — covers abandoned SMS attempts without stale reuse. */
const PENDING_CONTEXT_TTL_MS = 10 * 60 * 1000

type PendingPortalLoginContext = {
  enrollmentUserId: string
  phoneNumber?: string
  email?: string
  createdAtMs: number
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase()

const readPendingContext = (): PendingPortalLoginContext | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_LOGIN_CONTEXT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingPortalLoginContext
    if (typeof parsed.enrollmentUserId !== 'string' || typeof parsed.createdAtMs !== 'number') {
      return null
    }
    const hasPhone = typeof parsed.phoneNumber === 'string'
    const hasEmail = typeof parsed.email === 'string'
    if (!hasPhone && !hasEmail) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const readPendingEmailContext = (): PendingPortalLoginContext | null => {
  const fromSession = readPendingContext()
  if (fromSession?.email) {
    return fromSession
  }

  try {
    const raw = localStorage.getItem(PENDING_LOGIN_EMAIL_CONTEXT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingPortalLoginContext
    if (
      typeof parsed.enrollmentUserId !== 'string' ||
      typeof parsed.createdAtMs !== 'number' ||
      typeof parsed.email !== 'string'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const writePendingEmailContext = (payload: PendingPortalLoginContext): void => {
  const serialized = JSON.stringify(payload)
  try {
    sessionStorage.setItem(PENDING_LOGIN_CONTEXT_KEY, serialized)
  } catch {
    // ignore quota / private mode errors
  }
  try {
    localStorage.setItem(PENDING_LOGIN_EMAIL_CONTEXT_KEY, serialized)
  } catch {
    // ignore quota / private mode errors
  }
}

const clearPendingEmailContextStorage = (): void => {
  try {
    sessionStorage.removeItem(PENDING_LOGIN_CONTEXT_KEY)
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(PENDING_LOGIN_EMAIL_CONTEXT_KEY)
  } catch {
    // ignore
  }
}

/** Stash enrollmentUserId + phone from requestPortalLoginSMS for fast post-auth portal user setup. */
export const setPendingPortalLoginContext = (enrollmentUserId: string, phoneNumber: string): void => {
  try {
    const payload: PendingPortalLoginContext = {
      enrollmentUserId: enrollmentUserId.trim(),
      phoneNumber: phoneNumber.trim(),
      createdAtMs: Date.now()
    }
    sessionStorage.setItem(PENDING_LOGIN_CONTEXT_KEY, JSON.stringify(payload))
  } catch {
    // ignore quota / private mode errors
  }
}

/** Stash enrollmentUserId + email from email-link flow for fast post-auth portal user setup. */
export const setPendingPortalLoginContextWithEmail = (enrollmentUserId: string, email: string): void => {
  const payload: PendingPortalLoginContext = {
    enrollmentUserId: enrollmentUserId.trim(),
    email: normalizeEmail(email),
    createdAtMs: Date.now()
  }
  writePendingEmailContext(payload)
}

/**
 * Returns enrollmentUserId only when pending context matches the authenticated phone and is fresh.
 * Clears invalid or expired entries.
 */
export const consumePendingPortalEnrollmentUserId = (phoneNumber: string): string | null => {
  const context = readPendingContext()
  if (!context) {
    return null
  }

  const normalizedPhone = phoneNumber.trim()
  const isExpired = Date.now() - context.createdAtMs > PENDING_CONTEXT_TTL_MS
  const phoneMismatch = context.phoneNumber !== normalizedPhone
  const enrollmentUserId = context.enrollmentUserId.trim()

  if (isExpired || phoneMismatch || enrollmentUserId.length === 0) {
    clearPendingPortalLoginContext()
    return null
  }

  clearPendingPortalLoginContext()
  return enrollmentUserId
}

/**
 * Returns enrollmentUserId only when pending context matches the authenticated email and is fresh.
 * Clears invalid or expired entries.
 */
export const consumePendingPortalEnrollmentUserIdByEmail = (email: string): string | null => {
  const context = readPendingEmailContext()
  if (!context) {
    return null
  }

  const normalizedEmail = normalizeEmail(email)
  const isExpired = Date.now() - context.createdAtMs > PENDING_CONTEXT_TTL_MS
  const emailMismatch = normalizeEmail(context.email ?? '') !== normalizedEmail
  const enrollmentUserId = context.enrollmentUserId.trim()

  if (isExpired || emailMismatch || enrollmentUserId.length === 0) {
    clearPendingPortalLoginContext()
    return null
  }

  clearPendingPortalLoginContext()
  return enrollmentUserId
}

export const clearPendingPortalLoginContext = (): void => {
  clearPendingEmailContextStorage()
}

export const setPortalAuthErrorCode = (code: PortalAuthErrorCode): void => {
  try {
    sessionStorage.setItem(PORTAL_AUTH_ERROR_KEY, code)
  } catch {
    // ignore
  }
}

export const consumePortalAuthErrorCode = (): PortalAuthErrorCode | null => {
  try {
    const raw = sessionStorage.getItem(PORTAL_AUTH_ERROR_KEY)
    sessionStorage.removeItem(PORTAL_AUTH_ERROR_KEY)
    if (raw === PortalAuthErrorCode.ClaimsSyncFailed) {
      return PortalAuthErrorCode.ClaimsSyncFailed
    }
    return null
  } catch {
    return null
  }
}

export const clearPortalAuthErrorCode = (): void => {
  try {
    sessionStorage.removeItem(PORTAL_AUTH_ERROR_KEY)
  } catch {
    // ignore
  }
}
